// useChatSession — chat message state + the full send/receive/history flow.
//
// Extracted verbatim from `AIChatBot.js` (behavior-preserving refactor, issue
// #240). This hook owns everything the chat conversation needs EXCEPT the
// auto-scroll anchor (see `useScrollAnchor`) and the voice input (see
// `useVoiceInput`):
//
//   State:
//     - messages              (the rendered conversation)
//     - inputMessage          (the composer text)
//     - isLoading             (typing indicator / disabled controls)
//     - hotelSearchState      (null | awaiting_location | awaiting_brand | awaiting_budget)
//     - hotelSearchParams     (collected location/brand/budget)
//   Refs:
//     - inputRef              (composer textarea, re-focused after async flows)
//
// Effects (deps preserved exactly):
//     - welcome message on open  ([isOpen], eslint-disabled like the source)
//     - reset on close           ([isOpen])
//     - focus the composer on open ([isOpen])
//
// Service side effects, request bodies, endpoints, call COUNTS, and relative
// ORDER are preserved EXACTLY:
//     - AI chat: POST to AI_CHAT_API_BASE with { message, tripData,
//       conversationHistory: messages.slice(-10) }; on a non-ok response or
//       `success === false` it throws and the catch renders the graceful
//       "having trouble connecting" fallback (console.error logged).
//     - AI-signalled dining: when the AI response carries `diningSearch.city`,
//       a follow-up POST to RESTAURANT_AI_API_BASE is issued AFTER the AI chat
//       call (order asserted by the 8.1 parity test).
//     - guided hotel flow: location → brand → budget, then
//       getHotelRecommendations(...) FOLLOWED BY learnSignal('hotel_search', ...)
//       in that order; hotel-fetch failures render the "trouble finding hotels"
//       fallback (console.error logged).
//
// All `useCallback` dependency arrays match the source. Pure logic is reused
// from `../logic/` (buildWelcomeMessage with injected `now`, deriveTripContext,
// resolveHotelFlowAction, resolveLocationStepAction, buildHotelSearchParams,
// buildHotelResultMessage, buildRestaurantSearchParams, getInputPlaceholder,
// formatMessageTime) and the shared hotel-flow option constants, rather than
// duplicating them.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useRef, useEffect, useCallback } from 'react';
import { AI_CHAT_API_BASE, RESTAURANT_AI_API_BASE } from '../../../config/environment';
import { authFetch } from '../../../utils/authFetch';
import { getHotelRecommendations } from '../../../services/hotelService';
import { learnSignal } from '../../../services/tasteProfileService';

import buildWelcomeMessage from '../logic/buildWelcomeMessage';
import deriveTripContext from '../logic/deriveTripContext';
import resolveHotelFlowAction from '../logic/resolveHotelFlowAction';
import resolveLocationStepAction from '../logic/resolveLocationStepAction';
import buildHotelSearchParams from '../logic/buildHotelSearchParams';
import buildHotelResultMessage from '../logic/buildHotelResultMessage';
import buildRestaurantSearchParams from '../logic/buildRestaurantSearchParams';
import getInputPlaceholder from '../logic/getInputPlaceholder';
import formatMessageTime from '../logic/formatMessageTime';
import { LOCATION_OPTIONS, BRAND_OPTIONS, BUDGET_OPTIONS } from '../logic/hotelFlowOptions';

/**
 * Owns the chat conversation state and the send/receive/history flow.
 *
 * @param {Object} params
 * @param {boolean} params.isOpen - Whether the chat overlay is open. Drives the
 *   welcome/reset/focus effects (same trigger as the source).
 * @param {Object|null|undefined} params.tripData - The trip record used to derive
 *   context for the welcome message, AI chat payload, and hotel search.
 * @param {Object|null|undefined} params.userProfile - The user profile, used to
 *   resolve a saved hotel brand during the location step.
 * @returns {{
 *   messages: Array,
 *   inputMessage: string,
 *   setInputMessage: (value: string) => void,
 *   isLoading: boolean,
 *   hotelSearchState: (null|'awaiting_location'|'awaiting_brand'|'awaiting_budget'),
 *   inputRef: import('react').RefObject<HTMLTextAreaElement>,
 *   inputPlaceholder: string,
 *   formatTime: (timestamp: Date) => string,
 *   handleSendMessage: (text?: string) => Promise<void>,
 *   handleQuickReply: (value: string) => void,
 *   handleKeyPress: (e: import('react').KeyboardEvent) => void
 * }} The chat session API consumed by the container.
 */
export function useChatSession({ isOpen, tripData, userProfile }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Hotel guided search state machine
  const [hotelSearchState, setHotelSearchState] = useState(null);
  // null | 'awaiting_location' | 'awaiting_brand' | 'awaiting_budget'
  const [hotelSearchParams, setHotelSearchParams] = useState({});

  const inputRef = useRef(null);

  // ── Welcome message ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([buildWelcomeMessage(tripData, new Date())]);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setHotelSearchState(null);
      setHotelSearchParams({});
    }
  }, [isOpen]);

  // Focus input
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // ── Add bot message helper ──────────────────────────────────────────────────
  const addBotMessage = useCallback((content, quickReplies = null, cards = null) => {
    setMessages(prev => [...prev, {
      id: Date.now() + Math.random(),
      type: 'bot',
      content,
      quickReplies,
      cards,
      timestamp: new Date()
    }]);
  }, []);

  // ── Hotel fetch + display (extracted from handleHotelFlow to reduce complexity)
  const fetchAndDisplayHotels = useCallback(async (finalParams, ctx) => {
    addBotMessage('Searching hotels for you... 🔍');
    setIsLoading(true);

    try {
      const { destination, city, checkIn, checkOut, locationHint, brand } = buildHotelSearchParams(finalParams, ctx, tripData);

      const hotels = await getHotelRecommendations({
        city, checkIn, checkOut, guests: 1,
        budget: finalParams.budget,
        locationHint,
        brand
      });

      const resultMsg = buildHotelResultMessage(finalParams, destination);

      setMessages(prev => {
        const withoutLoading = prev.slice(0, -1);
        return [
          ...withoutLoading,
          {
            id: Date.now() + Math.random(),
            type: 'bot',
            content: resultMsg,
            cards: { type: 'hotel', items: hotels },
            timestamp: new Date()
          }
        ];
      });

      learnSignal('hotel_search', {
        brand: finalParams.brand,
        budgetTier: finalParams.budget,
        locationHint: finalParams.locationHint,
        city: destination,
      });
    } catch (err) {
      console.error('[AIChatBot] Hotel fetch error:', err);
      setMessages(prev => {
        const withoutLoading = prev.slice(0, -1);
        return [
          ...withoutLoading,
          {
            id: Date.now() + Math.random(),
            type: 'bot',
            content: "Sorry, I had trouble finding hotels right now. Try again in a moment! 🏨",
            timestamp: new Date()
          }
        ];
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [tripData, addBotMessage]);

  // ── Handle location step: collect location, decide brand or skip ────────────
  const handleLocationStep = useCallback((userText) => {
    const { locationHint, savedBrand } = resolveLocationStepAction(userText, userProfile);

    setHotelSearchParams(prev => ({ ...prev, locationHint }));

    if (!savedBrand) {
      setHotelSearchState('awaiting_brand');
      addBotMessage(
        `Got it — ${locationHint}!\n\nDo you have a preferred hotel brand?`,
        BRAND_OPTIONS
      );
      return;
    }

    setHotelSearchState('awaiting_budget');
    setHotelSearchParams(prev => ({ ...prev, brand: savedBrand }));
    addBotMessage(
      `Got it — ${locationHint}!\n\nI see you prefer ${savedBrand} from your profile. Stick with ${savedBrand}, or try other brands?`,
      [
        { label: `🔵 Keep ${savedBrand}`, value: savedBrand },
        { label: '🌐 Show all brands', value: 'no preference' },
      ]
    );
  }, [userProfile, addBotMessage]);

  // ── Hotel search flow handler ───────────────────────────────────────────────
  const handleHotelFlow = useCallback(async (userText) => {
    const result = resolveHotelFlowAction(hotelSearchState, userText);
    if (result.action === 'none') return false;

    if (result.action === 'collect_location') {
      handleLocationStep(userText);
    } else if (result.action === 'start_location') {
      setHotelSearchState('awaiting_location');
      setHotelSearchParams({});
      addBotMessage('Great! To find the best options, where would you like to stay?', LOCATION_OPTIONS);
    } else if (result.action === 'collect_brand') {
      setHotelSearchParams(prev => ({ ...prev, brand: result.brand }));
      setHotelSearchState('awaiting_budget');
      addBotMessage(`Perfect — ${result.brandDisplay}! What's your nightly budget?`, BUDGET_OPTIONS);
    } else if (result.action === 'collect_budget') {
      const ctx = deriveTripContext(tripData);
      const finalParams = { ...hotelSearchParams, budget: result.budget };
      setHotelSearchParams(finalParams);
      setHotelSearchState(null);
      await fetchAndDisplayHotels(finalParams, ctx);
    }
    return true;
  }, [hotelSearchState, hotelSearchParams, tripData, addBotMessage, fetchAndDisplayHotels, handleLocationStep]);

  // ── Restaurant fetch + display (extracted from handleSendMessage to reduce complexity)
  const fetchAndDisplayRestaurants = useCallback(async (diningSearch) => {
    setIsLoading(true);
    try {
      const searchParams = buildRestaurantSearchParams(diningSearch);

      const restaurantResponse = await authFetch(RESTAURANT_AI_API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(searchParams)
      });

      if (!restaurantResponse.ok) {
        addBotMessage("Sorry, I had trouble searching for restaurants. Try again in a moment!");
        return;
      }

      const restaurantData = await restaurantResponse.json();
      if (restaurantData.success && restaurantData.restaurants?.length > 0) {
        addBotMessage(
          `Here are ${restaurantData.restaurants.length} spots I found:`,
          null,
          { type: 'restaurant', items: restaurantData.restaurants }
        );
      } else {
        addBotMessage("I couldn't find restaurants matching that criteria. Could you try a different location or broaden your preferences?");
      }
    } catch (restaurantError) {
      console.error('[AIChatBot] Restaurant search error:', restaurantError);
      addBotMessage("Sorry, the restaurant search hit a snag. Try again in a moment!");
    } finally {
      setIsLoading(false);
    }
  }, [addBotMessage]);

  // ── Check if message matches a hotel intent keyword ──────────────────────
  const isHotelIntent = useCallback((text) => {
    const lc = text.toLowerCase();
    return lc.includes('hotel') || lc.includes('stay') || lc.includes('accommodation');
  }, []);

  // ── Fall back to Bedrock AI chat ────────────────────────────────────────
  const fallbackToAIChat = useCallback(async (msgText) => {
    setIsLoading(true);
    try {
      const response = await authFetch(AI_CHAT_API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msgText,
          tripData,
          conversationHistory: messages.slice(-10)
        })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Unknown error');

      addBotMessage(data.response);

      // If AI signaled a dining search, fetch real restaurant results
      if (data.diningSearch && data.diningSearch.city) {
        await fetchAndDisplayRestaurants(data.diningSearch);
      }
    } catch (error) {
      console.error('[AIChatBot] AI chat error:', error);
      addBotMessage("Sorry, I'm having trouble connecting right now. Please try again! 🤖");
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, tripData, addBotMessage, fetchAndDisplayRestaurants]);

  // ── Main send handler ───────────────────────────────────────────────────────
  const handleSendMessage = useCallback(async (text) => {
    const msgText = (text || inputMessage).trim();
    if (!msgText || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: msgText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');

    // Check hotel flow first
    const handledByHotelFlow = await handleHotelFlow(msgText);
    if (handledByHotelFlow) return;

    // Check if this is a hotel intent that should start the flow
    if (isHotelIntent(msgText)) {
      const handledByHotelStart = await handleHotelFlow(msgText);
      if (handledByHotelStart) return;
    }

    // Fall back to Bedrock AI chat
    await fallbackToAIChat(msgText);
  }, [inputMessage, isLoading, handleHotelFlow, isHotelIntent, fallbackToAIChat]);

  // ── Quick reply handler ─────────────────────────────────────────────────────
  const handleQuickReply = useCallback((value) => {
    handleSendMessage(value);
  }, [handleSendMessage]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const inputPlaceholder = getInputPlaceholder(hotelSearchState);

  return {
    messages,
    inputMessage,
    setInputMessage,
    isLoading,
    hotelSearchState,
    inputRef,
    inputPlaceholder,
    formatTime: formatMessageTime,
    handleSendMessage,
    handleQuickReply,
    handleKeyPress,
  };
}

export default useChatSession;
